/**
 * The Reference Ledger — the heart of candidate-web (design-spec/05 Screen 01).
 *
 * One recency-sorted list of every request and its live state, rendered as the seven card states.
 * Four screen states: loading (a breathing brand mark, never a dead spinner), error (calm retry),
 * empty ("Your first reference is on us."), and the populated list with the conditional gold summary
 * strip + the faint thread behind the cards. The "Request a reference" CTA is bottom-anchored and
 * persistent over scroll (thumb zone), a labelled button — never a FAB.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { readyCount } from '@/lib/ledger';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { ThreadMark } from '@/components/ThreadMark';
import { ReferenceCard } from '@/components/ledger/ReferenceCard';
import { SummaryStrip } from '@/components/ledger/SummaryStrip';
import { LedgerThread } from '@/components/ledger/LedgerThread';

const requestCta = (label: string) => (
  <Button asChild size="block">
    <Link to="/request">{label}</Link>
  </Button>
);

function LoadingState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-20" aria-live="polite" aria-busy="true">
      <ThreadMark className="brand-pulse h-6 w-auto" />
      <p className="mt-3 font-body text-[13px] text-ink-quiet">Loading your ledger…</p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-20 text-center" role="alert">
      <h2 className="font-display text-[18px] font-semibold tracking-[-0.02em] text-ink">
        We couldn’t load your ledger
      </h2>
      <p className="mt-2 max-w-[32ch] font-body text-[14px] leading-relaxed text-ink-quiet">
        Something went wrong reaching By My Word. Check your connection and try again.
      </p>
      <Button variant="ghost" className="mt-5" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-20 text-center">
      <ThreadMark className="h-6 w-auto" />
      <h2 className="mt-5 font-display text-[21px] font-semibold tracking-[-0.02em] text-ink">
        No references yet
      </h2>
      <p className="mt-2 max-w-[30ch] font-body text-[14px] leading-relaxed text-ink-quiet">
        Ask someone who knows your work. Your first reference is on us.
      </p>
    </div>
  );
}

export function Ledger() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['ledger'],
    queryFn: () => apiClient.getLedger(),
  });

  if (isPending) {
    return (
      <AppShell title="References Ledger">
        <LoadingState />
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell title="References Ledger" footer={requestCta('Request a reference')}>
        <ErrorState onRetry={() => void refetch()} />
      </AppShell>
    );
  }

  if (data.length === 0) {
    return (
      <AppShell title="References Ledger" footer={requestCta('Request your first reference')}>
        <EmptyState />
      </AppShell>
    );
  }

  return (
    <AppShell title="References Ledger" footer={requestCta('Request a reference')}>
      {/* The screen's programmatic h1 — the topbar carries only a decorative label span, so the
          populated list needs its own heading (the other screens each render one in content). */}
      <h1 className="mb-3.5 font-display text-[19px] font-semibold tracking-[-0.02em] text-ink">
        Your references
      </h1>
      <SummaryStrip count={readyCount(data)} />
      <div className="relative pb-4">
        <LedgerThread />
        <ul className="relative z-[1] flex flex-col gap-2">
          {data.map((card) => (
            <li key={card.id}>
              <ReferenceCard card={card} />
            </li>
          ))}
        </ul>
      </div>
    </AppShell>
  );
}
